import { useHistory } from 'react-router-dom';
import { ClarityEvents } from '../../../lib/clarity/types/clarityEvents.types';
import { captureEvent, captureEventWithName } from '../../../lib/clarity/utils/captureEvent.utils';
import { ProductSearchResultItem, SearchableEntityType } from '../globalSearch.types';
import { buildCatalogDetailRoute } from '../lib/entityConfig';
import { resolveEntityListRoute, resolveRoute } from '../lib/resolveRoute';
import { GlobalSearchListItem } from '../staticCommands/staticCommands.types';

interface UseGlobalSearchResultNavigationParams {
  isStore: boolean;
  debouncedQuery: string;
  activeEntityType: SearchableEntityType | null;
  canOpenEntity: (entityType: SearchableEntityType) => boolean;
  canUseItemAction: (entityType: SearchableEntityType) => boolean;
  hasSelectedOrder: boolean;
  recordRecentSearch: (term?: string) => void;
  openProductForView: (item: ProductSearchResultItem) => void;
  addProductToOrder: (item: ProductSearchResultItem) => void;
  onClose: () => void;
}

const useGlobalSearchResultNavigation = ({
  isStore,
  debouncedQuery,
  activeEntityType,
  canOpenEntity,
  canUseItemAction,
  hasSelectedOrder,
  recordRecentSearch,
  openProductForView,
  addProductToOrder,
  onClose,
}: UseGlobalSearchResultNavigationParams) => {
  const history = useHistory();

  const handleSelectItem = (listItem: GlobalSearchListItem) => {
    if (listItem.kind === 'command') {
      captureEventWithName(
        ClarityEvents.GLOBAL_SEARCH_SELECTED_COMMAND_WITH_ID,
        listItem.command.id,
      );
      recordRecentSearch(debouncedQuery);
      listItem.command.dispatch();
      return;
    }
    const { item } = listItem;
    if (!canOpenEntity(item.entityType)) {
      return;
    }
    captureEventWithName(ClarityEvents.GLOBAL_SEARCH_SELECTED_RESULT_WITH_ENTITY, item.entityType);
    recordRecentSearch();
    onClose();
    if (item.entityType === 'product') {
      openProductForView(item);
      return;
    }
    history.push(resolveRoute(item, { isStore }));
  };

  const handleAddProductToOrder = (item: ProductSearchResultItem) => {
    if (!canOpenEntity('product') || !canUseItemAction('product')) {
      return;
    }
    captureEvent(ClarityEvents.GLOBAL_SEARCH_ADDED_PRODUCT_TO_ORDER);
    recordRecentSearch();
    if (!hasSelectedOrder) {
      onClose();
    }
    addProductToOrder(item);
  };

  const handleNavigateToList = () => {
    if (!activeEntityType) {
      return;
    }
    captureEventWithName(
      ClarityEvents.GLOBAL_SEARCH_NAVIGATED_TO_LIST_WITH_ENTITY,
      activeEntityType,
    );
    recordRecentSearch(debouncedQuery);
    history.push(
      resolveEntityListRoute({ entityType: activeEntityType, term: debouncedQuery, isStore }),
    );
    onClose();
  };

  const handleSelectSuggestedCatalog = (catalogId: string) => {
    if (!canOpenEntity('catalog')) {
      return;
    }
    captureEvent(ClarityEvents.GLOBAL_SEARCH_SELECTED_SUGGESTED_CATALOG);
    history.push(buildCatalogDetailRoute(catalogId));
    onClose();
  };

  return {
    handleSelectItem,
    handleAddProductToOrder,
    handleNavigateToList,
    handleSelectSuggestedCatalog,
  };
};

export default useGlobalSearchResultNavigation;
